import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import pordefectopiloto from "../pordefectopiloto.png";
import { FranjaFija } from "./franjaFija";

const POR_PAGINA = 9;
const ANCHO_DISENO = 1280;

// Decoraciones exportadas de Figma: [número de imagen, x, y, ancho, alto] en píxeles del diseño (1280 de ancho),
// medidos desde la esquina superior izquierda de la sección. Se pasan a cqw para que escalen con la sección.
const DECORACIONES = [
  [44, 113, 558, 68, 67],
  [45, 362, 279, 118, 67],
  [46, 1143, 581, 47, 30],
  [46, 646, 570, 47, 30],
  [47, 349, 1211, 89, 57],
  [48, 830, 972, 77, 49],
  [49, 782, 173, 157, 278],
  [50, 1166, 1237, 157, 278],
  [51, 133, 1350, 103, 277],
  [52, 392, 509, 158, 277],
  [53, 827, 589, 157, 278],
];

const aCqw = (px) => `${(px / ANCHO_DISENO) * 100}cqw`;

// Barras y bandas de fondo. Tiene que estar dentro de una sección con container-type (ver .pilotos-asoc)
export const DecoracionesPilotos = () =>
  DECORACIONES.map(([n, x, y, w, h], i) => (
    <img
      key={`${n}-${i}`}
      src={`/img/asocpil-${n}.png`}
      alt=""
      aria-hidden="true"
      className="pilotos-asoc-deco"
      style={{ left: aCqw(x), top: aCqw(y), width: aCqw(w), height: aCqw(h) }}
    />
  ));

// Números de página a mostrar: todas si son pocas; si son muchas, la primera, la última y las cercanas a la actual
const paginasVisibles = (actual, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => ({ n: i + 1 }));
  const numeros = [...new Set([1, total, actual - 1, actual, actual + 1])]
    .filter((n) => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const resultado = [];
  numeros.forEach((n, i) => {
    if (i > 0 && n - numeros[i - 1] > 1) resultado.push({ puntos: true, key: `p${n}` });
    resultado.push({ n });
  });
  return resultado;
};

export const Paginacion = ({ pagina, total, onCambiar }) => {
  if (total <= 1) return null;
  return (
    <nav className="pilotos-asoc-paginas" aria-label="Páginas de pilotos">
      <button type="button" disabled={pagina === 1} onClick={() => onCambiar(pagina - 1)} aria-label="Página anterior">
        <LeftOutlined />
      </button>
      {paginasVisibles(pagina, total).map((item) =>
        item.puntos ? (
          <span key={item.key}>…</span>
        ) : (
          <button
            key={item.n}
            type="button"
            className={item.n === pagina ? "activo" : ""}
            onClick={() => onCambiar(item.n)}
          >
            {item.n}
          </button>
        )
      )}
      <button
        type="button"
        disabled={pagina === total}
        onClick={() => onCambiar(pagina + 1)}
        aria-label="Página siguiente"
      >
        <RightOutlined />
      </button>
    </nav>
  );
};

// Tarjeta de piloto. Con mostrarNumero={false} (cuando la API no trae número) el lado oscuro queda liso
export const TarjetaPiloto = ({ piloto, idAsociacion, mostrarNumero = true }) => {
  const imagen = piloto.imagen
    ? `${process.env.REACT_APP_DOMINIO_ARCHIVOS}/${idAsociacion}/IMAGENES/PILOTOS/${piloto.imagen}`
    : pordefectopiloto;
  const nombre = `${piloto.nombre || ""} ${piloto.apellido || ""}`.replace(/\s+/g, " ").trim() || "Nombre no disponible";

  return (
    <article className="piloto-card" translate="no">
      <div className="piloto-card-foto">
        <img
          src={imagen}
          alt={nombre}
          onError={(e) => {
            // si la foto no carga, se usa la silueta (una sola vez, para no entrar en un ciclo)
            if (!e.currentTarget.dataset.reintento) {
              e.currentTarget.dataset.reintento = "1";
              e.currentTarget.src = pordefectopiloto;
            }
          }}
        />
        {mostrarNumero && <span className="piloto-card-numero"># {piloto.numero_piloto ?? "-"}</span>}
      </div>
      <div className="piloto-card-info">
        <h3>{nombre}</h3>
        <p>
          <span className="piloto-card-raya" />
          {piloto.localidad || "Sin localidad"}
        </p>
      </div>
    </article>
  );
};

// Sección de pilotos registrados en la categoría y campeonato seleccionados
export const PilotosAsociacion = ({ pilotos = [], categoria, idAsociacion }) => {
  const [pagina, setPagina] = useState(1);
  const totalPaginas = Math.max(1, Math.ceil(pilotos.length / POR_PAGINA));

  // al cambiar de categoría o campeonato se vuelve a la primera página
  useEffect(() => {
    setPagina(1);
  }, [pilotos]);

  const visibles = pilotos.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  return (
    <FranjaFija izq="#0B2941">
    <section className="pilotos-asoc" id="pilotos">
      <DecoracionesPilotos />

      <div className="pilotos-asoc-interior">
        <div className="pilotos-asoc-cabecera">
          <span className="pilotos-asoc-barra" />
          <h2 className="pilotos-asoc-titulo">Pilotos</h2>
          {pilotos.length > 0 && (
            <Link to={`/pilotos/${idAsociacion}`} className="pilotos-asoc-ver-todos">
              Ver todos los pilotos <RightOutlined />
            </Link>
          )}
          <p className="pilotos-asoc-subtitulo">
            Pilotos registrados de la categoría <span>{categoria}</span>
          </p>
        </div>

        {pilotos.length > 0 ? (
          <>
            <div className="pilotos-asoc-grilla">
              {visibles.map((piloto) => (
                <TarjetaPiloto key={piloto.id} piloto={piloto} idAsociacion={idAsociacion} />
              ))}
            </div>
            <Paginacion pagina={pagina} total={totalPaginas} onCambiar={setPagina} />
          </>
        ) : (
          <p className="pilotos-asoc-vacio">No hay pilotos disponibles.</p>
        )}
      </div>
    </section>
    </FranjaFija>
  );
};
